import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface YouchuanVariationParams {
  /**
   * 作业ID，必填
   */
  jobId: string;
  /**
   * 图像编号，必填，范围0-3
   * @values 0 | 1 | 2 | 3
   */
  imageNo: 0 | 1 | 2 | 3;
  /**
   * 变体类型，必填，可选值：subtle(细微变化)、strong(强烈变化)
   * @values subtle | strong
   */
  type: 'subtle' | 'strong';
  /**
   * 回调URL，必须以http://或https://开头
   */
  callback?: string;
}

export interface YouchuanVariationResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * youchuan_variation
 *
 * 通过悠船图像变体API创建图像变体任务。
 *
 * API Documentation: https://tob.youchuan.cn/openapi/apis/%E5%9B%BE%E7%89%87%E7%94%9F%E6%88%90/api_variation
 *
 * @example
 * ```typescript
 * import { initSeacloud, youchuanVariation } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await youchuanVariation({
 *   jobId: 'your-job-id',
 *   imageNo: 0,
 *   type: 'subtle'
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 YouchuanVariationParams 接口定义
 * @returns 生成的资源列表
 */
export async function youchuanVariation(
  params: YouchuanVariationParams,
): Promise<YouchuanVariationResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'youchuan_variation',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: YouchuanVariationResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
